import React from 'react';
import dayjs from '../../../helpers/dates';
import { useParams } from 'react-router';
import { Flex } from '../../../design-system';
import { isEmpty } from 'lodash';
import { useEffectOnce, useToggle } from 'react-use';
import { Block, Button, Card, Container, Text } from '../../../design-system';

import ContainerHeader from '../../../components/ContainerHeader';
import { useData } from '../../../helpers/data-loader';
import { errorMsg } from '../../../helpers/utils';
import AgencyForm from './agencyForm';
import ImportFormModal from './importFormModal';
import ClientTransferFormModal from './ClientTransferFormModal';

const AgencyEdit = () => {
  const { id } = useParams();

  const [isOpenImportModal, toggleOpenImportModal] = useToggle(false);
  const [isOpenClientTransferModal, toggleOpenClientTransferModal] =
    useToggle(false);

  const [agency, fetchAgencyData, agencyLoading] = useData(
    `/admin/agency/${id}?clients=1`,
    {
      errorMsg: errorMsg('Agency'),
    },
  );

  const [agencies, fetchAgencies, agenciesLoading] = useData('/admin', {
    errorMsg: errorMsg('Agencies'),
  });

  useEffectOnce(() => {
    fetchAgencyData();
    fetchAgencies();
  });

  return (
    <>
      {!isEmpty(agency.agency) && !agencyLoading && !agenciesLoading && (
        <Container>
          <Card mt={30}>
            <ContainerHeader>
              <Text fontWeight={700} fontSize={25}>
                {!isEmpty(agency.agency) && <>{agency.agency.name}</>}
              </Text>
              <Flex justifyContent="space-between" width="25%" mr={30}>
                <Button onClick={toggleOpenClientTransferModal}>
                  Transfer Client
                </Button>
                <Button onClick={toggleOpenImportModal}>Import</Button>
              </Flex>
            </ContainerHeader>
          </Card>

          <Card mt={30}>
            <AgencyForm agency={agency.agency} />
          </Card>
          <Card mt={30} mb={30}>
            <dl className="mx-auto grid grid-cols-1 gap-px sm:grid-cols-2 divide-x divide-gray-200 lg:grid-cols-5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-4 sm:px-6 xl:px-8">
                <dt className="text-md font-medium text-gray-500">Clients</dt>
                <dd className="w-full flex-none text-3xl/10 font-medium tracking-tight text-gray-900">
                  {agency.clientCount || 0}
                </dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-4 sm:px-6 xl:px-8">
                <dt className="text-md font-medium text-gray-500">Projects</dt>
                <dd className="w-full flex-none text-3xl/10 font-medium tracking-tight text-gray-900">
                  {agency.projectCount || 0}
                </dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-4 sm:px-6 xl:px-8">
                <dt className="text-md font-medium text-gray-500">Funders</dt>
                <dd className="w-full flex-none text-3xl/10 font-medium tracking-tight text-gray-900">
                  {agency.funderCount || 0}
                </dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-4 sm:px-6 xl:px-8">
                <dt className="text-md font-medium text-gray-500">Contacts</dt>
                <dd className="w-full flex-none text-3xl/10 font-medium tracking-tight text-gray-900">
                  {agency.contactCount || 0}
                </dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-4 sm:px-6 xl:px-8">
                <dt className="text-md font-medium text-gray-500">Actions</dt>
                <dd className="w-full flex-none text-3xl/10 font-medium tracking-tight text-gray-900">
                  {agency.actionCount || 0}
                </dd>
              </div>
            </dl>
          </Card>
          <ClientTransferFormModal
            fetchAgencyData={fetchAgencyData}
            agencyClients={agency.clients}
            agencies={agencies}
            agencyID={id}
            isOpenClientTransferModal={isOpenClientTransferModal}
            toggleOpenClientTransferModal={toggleOpenClientTransferModal}
          />
          <ImportFormModal
            fetchAgencyData={fetchAgencyData}
            agencyID={id}
            isOpenImportModal={isOpenImportModal}
            toggleOpenImportModal={toggleOpenImportModal}
          />

          {agency.imports.length > 0 && (
            <Card mb={30}>
              <Text fontWeight={700} fontSize={25} mb={10}>
                Imports
              </Text>
              <ul className="divide-y divide-gray-300 p-4 bg-gray-50 rounded-lg">
                {agency.imports.map((importItem) => (
                  <li
                    key={importItem.id || importItem.filename}
                    className="py-5"
                  >
                    <div className="min-w-0">
                      <div className="flex items-start gap-x-3">
                        <p className="text-sm/6 font-semibold text-gray-900">
                          {importItem.filename}
                        </p>
                        {importItem.status === 'Failed' ? (
                          <p className="mt-0.5 rounded-md bg-red-50 px-1.5 py-0.5 text-xs font-medium whitespace-nowrap text-red-700 ring-1 ring-red-600/20 ring-inset">
                            {importItem.status}
                          </p>
                        ) : (
                          <p className="mt-0.5 rounded-md bg-blue-50 px-1.5 py-0.5 text-xs font-medium whitespace-nowrap text-blue-700 ring-1 ring-blue-600/20 ring-inset">
                            {importItem.status}
                          </p>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
                        <p className="whitespace-nowrap">
                          {dayjs.utc(importItem.updated_at).fromNow()}
                        </p>
                      </div>
                    </div>
                    {importItem.status === 'Failed' && (
                      <div className="ring-1 ring-red-600/20 bg-red-50 p-2 mt-3 bg-flex items-center gap-x-2 text-xs/5 text-gray-500">
                        {importItem.notes.split('\n').map((note, index) => (
                          <p key={`${importItem.id}-note-${index}`}>{note}</p>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </Container>
      )}
    </>
  );
};

export default AgencyEdit;
